/**
 * Admin-Bereich - Systemverwaltung
 * Sub-Komponenten extrahiert in /components/admin/
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '@/contexts/TabContext';
import {
  Settings, Database, FileText, Shield, Plus, Edit, 
  RefreshCw, Check, X, Sparkles, Plug, Lightbulb, 
  Brain, ArrowRight, Users, Building2, CalendarDays, Bot, FileSpreadsheet,
  Activity, Mail, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

// Extrahierte Sub-Komponenten
import EinstellungenTab from '@/components/admin/EinstellungenTab';
import KommunikationSettingsNew from '@/components/admin/KommunikationSettingsNew';
import FeatureRequestList from '@/components/admin/FeatureRequests';
import BankingSettings from '@/components/admin/BankingSettings';
import MonatsprotokollSettings from '@/components/admin/MonatsprotokollSettings';
import MCPSettings from '@/components/admin/MCPSettings';
import Butler21ImportTab from '@/components/admin/Butler21ImportTab';
import RecrawlTab from '@/components/admin/RecrawlTab';

const KATEGORIEN = [
  { value: 'einnahme', label: 'Einnahme', color: 'bg-green-100 text-green-700' },
  { value: 'ausgabe', label: 'Ausgabe', color: 'bg-red-100 text-red-700' },
  { value: 'versicherung', label: 'Versicherung', color: 'bg-blue-100 text-blue-700' },
  { value: 'bankkonto', label: 'Bankkonto', color: 'bg-purple-100 text-purple-700' },
  { value: 'glaeubigerverbindung', label: 'Glaeubigerverbindung', color: 'bg-amber-100 text-amber-700' },
];

function MassnahmenTypForm({ item, onClose, onSave }) {
  const [formData, setFormData] = useState({
    key: item?.key || '', name: item?.name || '',
    kategorie: item?.kategorie || 'einnahme', beschreibung: item?.beschreibung || '',
    aktiv: item?.aktiv ?? true,
  });
  const [saving, setSaving] = useState(false);
  const isEditing = !!item;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.key || !formData.name) { toast.error('Key und Name sind erforderlich'); return; }
    setSaving(true);
    try {
      toast.success(isEditing ? 'Gespeichert' : 'Erstellt');
      onSave();
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="key">Key (eindeutig)</Label>
          <Input id="key" value={formData.key}
            onChange={(e) => setFormData(p => ({ ...p, key: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
            placeholder="z.b. alg1" disabled={isEditing} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Anzeigename</Label>
          <Input id="name" value={formData.name}
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
            placeholder="z.B. Arbeitslosengeld I" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Kategorie</Label>
        <Select value={formData.kategorie} onValueChange={(v) => setFormData(p => ({ ...p, kategorie: v }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {KATEGORIEN.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="beschreibung">Beschreibung</Label>
        <Input id="beschreibung" value={formData.beschreibung}
          onChange={(e) => setFormData(p => ({ ...p, beschreibung: e.target.value }))}
          placeholder="Kurze Beschreibung..." />
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
        <Button type="submit" disabled={saving}>{isEditing ? 'Speichern' : 'Erstellen'}</Button>
      </div>
    </form>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [activeTab, setActiveTab] = useState('massnahmenkatalog');
  const [katalog, setKatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [recrawlVisible, setRecrawlVisible] = useState(true);

  const checkRecrawlVisible = async () => {
    try {
      const token = localStorage.getItem('olympus_token');
      const sameOriginBase = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
      const res = await fetch(`${sameOriginBase}/api/aktenuebernahme-v2/recrawl-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecrawlVisible(!data.ausgeblendet);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { checkRecrawlVisible(); }, []);

  const loadKatalog = async () => {
    setLoading(true);
    try {
      const result = await betreuungAPI.listMassnahmenkatalog({});
      setKatalog(Array.isArray(result) ? result : []);
    } catch { console.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadKatalog(); }, []);

  const initDefaults = async () => {
    try {
      const result = await betreuungAPI.initMassnahmenkatalog();
      toast.success(result.message);
      loadKatalog();
    } catch { toast.error('Fehler beim Initialisieren'); }
  };

  const getKategorieLabel = (kategorie) => KATEGORIEN.find(k => k.value === kategorie)?.label || kategorie;
  const getKategorieColor = (kategorie) => KATEGORIEN.find(k => k.value === kategorie)?.color || 'bg-slate-100 text-slate-700';

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Settings className="w-6 h-6" /> Administration
          </h1>
          <p className="text-slate-500 mt-1">Systemeinstellungen und Stammdaten verwalten</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex w-full overflow-x-auto p-1 gap-1">
          <TabsTrigger value="massnahmenkatalog" className="flex items-center gap-2 shrink-0"><Database className="w-4 h-4" /> Massnahmen</TabsTrigger>
          <TabsTrigger value="athena" className="flex items-center gap-2 shrink-0"><Brain className="w-4 h-4" /> Zeo Brain</TabsTrigger>
          <TabsTrigger value="dubletten" className="flex items-center gap-2 shrink-0"><Users className="w-4 h-4" /> Dubletten</TabsTrigger>
          <TabsTrigger value="scan-review-bridge" className="flex items-center gap-2 shrink-0"><ShieldCheck className="w-4 h-4" /> Scan Review Bridge</TabsTrigger>
          <TabsTrigger value="kommunikation" className="flex items-center gap-2 shrink-0"><Plug className="w-4 h-4" /> Kommunikation</TabsTrigger>
          <TabsTrigger value="mcp" className="flex items-center gap-2 shrink-0"><Bot className="w-4 h-4" /> MCP/Claude</TabsTrigger>
          <TabsTrigger value="banking" className="flex items-center gap-2 shrink-0"><Building2 className="w-4 h-4" /> Banking</TabsTrigger>
          <TabsTrigger value="monatsprotokoll" className="flex items-center gap-2 shrink-0"><CalendarDays className="w-4 h-4" /> Monatsprotokoll</TabsTrigger>
          <TabsTrigger value="hephaistios" className="flex items-center gap-2 shrink-0"><FileText className="w-4 h-4" /> Hephaistion</TabsTrigger>
          <TabsTrigger value="enigma" className="flex items-center gap-2 shrink-0"><Shield className="w-4 h-4" /> Enigma</TabsTrigger>
          <TabsTrigger value="wunschliste" className="flex items-center gap-2 shrink-0"><Lightbulb className="w-4 h-4" /> Wunschliste</TabsTrigger>
          <TabsTrigger value="einstellungen" className="flex items-center gap-2 shrink-0"><Settings className="w-4 h-4" /> Einstellungen</TabsTrigger>
          <TabsTrigger value="butler21" className="flex items-center gap-2 shrink-0"><FileSpreadsheet className="w-4 h-4" /> Butler21 Import</TabsTrigger>
          {recrawlVisible && (
            <button
              onClick={() => openTab('/admin/recrawl', { title: 'Re-Crawl' })}
              className="flex items-center gap-2 shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
              data-testid="admin-recrawl-btn"
            >
              <RefreshCw className="w-4 h-4" /> Re-Crawl
            </button>
          )}
          <button
            onClick={() => openTab('/admin/monitoring', { title: 'Monitoring' })}
            className="flex items-center gap-2 shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
            data-testid="admin-monitoring-btn"
          >
            <Activity className="w-4 h-4" /> Monitoring
          </button>
          <button
            onClick={() => openTab('/admin/thunderbird', { title: 'Thunderbird' })}
            className="flex items-center gap-2 shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
            data-testid="admin-thunderbird-btn"
          >
            <Mail className="w-4 h-4" /> Thunderbird-Import
          </button>
        </TabsList>

        {/* Massnahmenkatalog */}
        <TabsContent value="massnahmenkatalog" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Verwalten Sie hier die verfuegbaren Massnahmentypen</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={initDefaults}><RefreshCw className="w-4 h-4 mr-2" /> Standard laden</Button>
              <Button onClick={() => setEditDialog({ open: true, item: null })}><Plus className="w-4 h-4 mr-2" /> Neuer Typ</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : katalog.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Key</TableHead><TableHead>Kategorie</TableHead><TableHead>Status</TableHead><TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {katalog.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-slate-500 font-mono text-sm">{item.key}</TableCell>
                      <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${getKategorieColor(item.kategorie)}`}>{getKategorieLabel(item.kategorie)}</span></TableCell>
                      <TableCell>{item.aktiv ? <span className="text-green-600 flex items-center gap-1"><Check className="w-4 h-4" /> Aktiv</span> : <span className="text-slate-400 flex items-center gap-1"><X className="w-4 h-4" /> Inaktiv</span>}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={() => setEditDialog({ open: true, item })}><Edit className="w-4 h-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Database className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Noch keine Massnahmentypen definiert</p>
                <Button onClick={initDefaults}><RefreshCw className="w-4 h-4 mr-2" /> Standardkatalog laden</Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Athena Brain */}
        <TabsContent value="athena" className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0"><Brain className="w-6 h-6 text-purple-600" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Zeo Brain - KI-Gedaechtnis</h3>
                <p className="text-slate-600 mt-1">Verwalten Sie das Wissen und die gelernten Muster von Zeo.</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/60 rounded-lg p-4"><p className="text-2xl font-bold text-purple-700">DSGVO-konform</p><p className="text-sm text-slate-500">Alle Daten pseudonymisiert</p></div>
                  <div className="bg-white/60 rounded-lg p-4"><p className="text-2xl font-bold text-purple-700">Lernfaehig</p><p className="text-sm text-slate-500">Verbessert sich stetig</p></div>
                  <div className="bg-white/60 rounded-lg p-4"><p className="text-2xl font-bold text-purple-700">Transparent</p><p className="text-sm text-slate-500">Wissen einsehbar & editierbar</p></div>
                </div>
                <div className="mt-6">
                  <Button onClick={() => navigate('/admin/athena-brain')} className="bg-purple-600 hover:bg-purple-700" data-testid="open-athena-brain-btn">
                    <Brain className="w-4 h-4 mr-2" /> Zeo Brain oeffnen <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h4 className="font-medium text-slate-800 flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-amber-500" /> Was Zeo lernt</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li>Dokumenttypen erkennen (Gericht, Behoerde, Inkasso...)</li>
                <li>Passende Aktionen vorschlagen (Aufgaben, Massnahmen)</li>
                <li>Prioritaeten einschaetzen</li>
                <li>Aus Benutzer-Feedback verbessern</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h4 className="font-medium text-slate-800 flex items-center gap-2 mb-3"><Shield className="w-5 h-5 text-green-500" /> Datenschutz</h4>
              <ul className="text-sm text-slate-600 space-y-2">
                <li>Keine echten Namen werden gespeichert</li>
                <li>Alle Daten werden pseudonymisiert</li>
                <li>Nur Muster werden gelernt, keine Inhalte</li>
                <li>DSGVO-konforme Verarbeitung</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* Scan Review Operator-Confirmation-Bridge */}
        <TabsContent value="scan-review-bridge" className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-emerald-600" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">ChatGPT Review-Paket (Scan Review Bridge)</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Von ChatGPT fachlich geprüfte Scan-Reviews als JSON-Paket einfügen, serverseitig
                  validieren und nach einmaliger Bestätigung über die bestehenden Fachaktionen anwenden.
                </p>
                <div className="mt-4">
                  <Button variant="default" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/admin/scan-review-bridge')} data-testid="open-scan-review-bridge-btn">
                    <ArrowRight className="w-4 h-4 mr-2" /> Scan Review Bridge öffnen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Dubletten */}
        <TabsContent value="dubletten" className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><Users className="w-6 h-6 text-amber-600" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Dubletten-Verwaltung</h3>
                <p className="text-sm text-slate-500 mt-1">Erkennung und Zusammenfuehrung von doppelten Personen-Eintraegen.</p>
                <div className="mt-4">
                  <Button variant="default" size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => navigate('/admin/dubletten')} data-testid="open-dubletten-btn">
                    <ArrowRight className="w-4 h-4 mr-2" /> Dubletten-Verwaltung oeffnen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Kommunikation */}
        <TabsContent value="kommunikation" className="space-y-4"><KommunikationSettingsNew /></TabsContent>

        {/* MCP/Claude */}
        <TabsContent value="mcp" className="space-y-4"><MCPSettings /></TabsContent>

        {/* Banking */}
        <TabsContent value="banking" className="space-y-4">
          <BankingSettings />
        </TabsContent>

        {/* Monatsprotokoll Auto-Versand */}
        <TabsContent value="monatsprotokoll" className="space-y-4">
          <MonatsprotokollSettings />
        </TabsContent>

        {/* Hephaistion */}
        <TabsContent value="hephaistios" className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><FileText className="w-6 h-6 text-amber-600" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Hephaistion - Dokumentenverarbeitung</h3>
                <p className="text-slate-500 mt-1">OCR-Texterkennung, PDF-Konvertierung und automatische Leerseiten-Entfernung</p>
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                  <p className="text-sm font-medium text-green-800">Status: Aktiv - laeuft direkt im System</p>
                </div>
                <div className="mt-4"><Button onClick={() => navigate('/ocr')} className="bg-amber-500 hover:bg-amber-600" data-testid="open-hephaistion-btn"><FileText className="w-4 h-4 mr-2" /> Hephaistion oeffnen <ArrowRight className="w-4 h-4 ml-2" /></Button></div>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-slate-700 text-sm font-medium mb-1">Agenten-Pipeline</p>
            <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
              <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded font-medium">Hephaistion (OCR)</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">Enigma (Datenschutz)</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">Zeo (Analyse)</span>
            </div>
          </div>
        </TabsContent>

        {/* Enigma */}
        <TabsContent value="enigma" className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0"><Shield className="w-6 h-6 text-purple-600" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800">Enigma - Datenschutz-Proxy</h3>
                <p className="text-slate-500 mt-1">DSGVO-konforme Pseudonymisierung personenbezogener Daten</p>
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                  <p className="text-sm font-medium text-green-800">Status: Aktiv - alle KI-Anfragen werden automatisch pseudonymisiert</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-purple-800">Datenschutz-Garantie</p>
                <p className="text-sm text-purple-700 mt-1">Keine personenbezogenen Daten werden unverschluesselt an externe KI-Dienste gesendet.</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Wunschliste */}
        <TabsContent value="wunschliste" className="space-y-4"><FeatureRequestList /></TabsContent>

        {/* Einstellungen */}
        <TabsContent value="einstellungen" className="space-y-4"><EinstellungenTab /></TabsContent>

        {/* Butler21 Import */}
        <TabsContent value="butler21" className="space-y-4"><Butler21ImportTab /></TabsContent>
        {/* Re-Crawl hat jetzt eine eigene Seite/Tab */}
      </Tabs>

      {/* Inline Edit Massnahmenkatalog */}
      {editDialog.open && (
        <Card className="max-w-lg mx-auto mt-4 border-blue-200 shadow-lg" data-testid="inline-massnahmen-edit">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{editDialog.item ? 'Massnahmentyp bearbeiten' : 'Neuer Massnahmentyp'}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setEditDialog({ open: false, item: null })}><X className="w-4 h-4" /></Button>
            </div>
            <CardDescription>Definieren Sie einen neuen Typ fuer Massnahmen und Leistungen</CardDescription>
          </CardHeader>
          <CardContent>
            <MassnahmenTypForm
              item={editDialog.item}
              onClose={() => setEditDialog({ open: false, item: null })}
              onSave={() => { setEditDialog({ open: false, item: null }); loadKatalog(); }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
